import React from 'react';

import { Button, Group, Modal, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconTags } from '@tabler/icons-react';
import useCreateClassCoachRole from 'hooks/classCoachRole/useCreateClassCoachRole';
import { KiloClassCoachRoleWrite } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface IModalCreateClassCoachRoleProps {
  opened: boolean;
  handleClose: () => void;
}

interface FormValues {
  name: string;
}

const ModalCreateClassCoachRole = ({
  opened,
  handleClose,
}: IModalCreateClassCoachRoleProps): JSX.Element => {
  const createMutation = useCreateClassCoachRole();

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
    },

    validate: {
      name: value =>
        value.length < 3 || value.length > 120 ? 'Name must be between 3-120 characters' : null,
    },
  });

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  const { successNotification, errorNotification } = notificationHandler();
  const handleCreate = (values: FormValues): void => {
    const payload: KiloClassCoachRoleWrite = {
      kiloClassCoachRoleId: uuidv4(),
      ...values,
    };
    createMutation.mutate(payload, {
      onSuccess: () => {
        successNotification(
          'Creating coach role for class success',
          `${values.name} role has been successfully created.`,
        );
        handleCloseAndReset();
      },
      onError: error => {
        errorNotification('Creating coach role for class failed', error.response?.data.message);
      },
    });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={4}>Create Coach Role For Classes</Title>}
      zIndex={201}
      size='auto'
    >
      <form onSubmit={form.onSubmit(handleCreate)}>
        <Stack>
          <TextInput
            required
            label='Name'
            {...form.getInputProps('name')}
            icon={<IconTags />}
            placeholder='Set Role Name'
            data-cy='classCoachRoleTextInput'
          />
          <Group position='right'>
            <Button variant='subtle' onClick={handleCloseAndReset} disabled={createMutation.isLoading}>
              Cancel
            </Button>
            <Button disabled={createMutation.isLoading} type='submit' data-cy='saveClassCoachRoleButton'>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ModalCreateClassCoachRole;
